import type { Route, Section, Segment } from '~/types/routes'
import type { ExpressionNode } from '~/types/filters'
import type { HighlightSegment, TreeViewElement } from '~/components/ui/file-tree'
import { sortByNumericName } from '~/utils/sort'

export const getRouteSections = (route: Route): Section[] => {
  return (route.children || []).flatMap((segment) => segment.children || [])
}

export const getAllRouteSections = (routes: Route[]): Section[] => {
  return routes.flatMap((route) => getRouteSections(route))
}

export const getRouteById = (routes: Route[], routeId: number): Route | undefined => {
  return routes.find((route) => route.id === routeId)
}

export const toHighlightSegments = (text: string, term: string): HighlightSegment[] => {
  const normalizedTerm = term.toLowerCase()
  if (!normalizedTerm) {
    return [{ text, match: false }]
  }

  const lowerText = text.toLowerCase()
  const segments: HighlightSegment[] = []

  let currentIndex = 0
  let nextIndex = lowerText.indexOf(normalizedTerm, currentIndex)

  while (nextIndex !== -1) {
    if (nextIndex > currentIndex) {
      segments.push({ text: text.slice(currentIndex, nextIndex) })
    }
    segments.push({ text: text.slice(nextIndex, nextIndex + term.length), match: true })
    currentIndex = nextIndex + term.length
    nextIndex = lowerText.indexOf(normalizedTerm, currentIndex)
  }

  if (currentIndex < text.length) {
    segments.push({ text: text.slice(currentIndex) })
  }

  return segments
}

export const formatRouteToTree = (route: Route, term: string): TreeViewElement | null => {
  const normalizedTerm = term.trim().toLowerCase()
  const hasTerm = normalizedTerm.length > 0
  const routeMatches = hasTerm && route.name.toLowerCase().includes(normalizedTerm)

  const baseSegments: TreeViewElement[] = sortByNumericName<Segment>(route.children || [], 'name').map(
    (segment: Segment) => ({
      id: segment.id,
      name: segment.name,
      children: sortByNumericName<Section>(segment.children || [], 'name').map((section: Section) => ({
        id: section.id,
        name: section.name
      }))
    })
  )

  if (!hasTerm) {
    return {
      id: route.id,
      name: route.name,
      children: baseSegments
    }
  }

  const matchingSegments = baseSegments
    .map((segment) => {
      const matchingSections = (segment.children || []).filter((section) =>
        section.name.toLowerCase().includes(normalizedTerm)
      )

      if (matchingSections.length === 0) {
        return null
      }

      return {
        id: segment.id,
        name: segment.name,
        childMatchCount: matchingSections.length,
        children: matchingSections.map((section) => ({
          id: section.id,
          name: section.name,
          highlightSegments: toHighlightSegments(section.name, term)
        }))
      } as TreeViewElement
    })
    .filter((segment): segment is TreeViewElement => segment !== null)

  if (!routeMatches && matchingSegments.length === 0) {
    return null
  }

  const childMatchCount = !routeMatches
    ? matchingSegments.reduce((count, segment) => count + (segment.children?.length || 0), 0)
    : undefined

  return {
    id: route.id,
    name: route.name,
    highlightSegments: routeMatches ? toHighlightSegments(route.name, term) : undefined,
    childMatchCount,
    children: routeMatches && matchingSegments.length === 0 ? baseSegments : matchingSegments
  }
}

export const ROUTE_LEVEL_PROPERTIES = ['wholeRouteId', 'routeSegmentId', 'routeId'] as const

export type RouteLevelProperty = (typeof ROUTE_LEVEL_PROPERTIES)[number]

export type RouteLevelSelection = Record<RouteLevelProperty, number[]>

export const isRouteLevelProperty = (property: string): property is RouteLevelProperty =>
  (ROUTE_LEVEL_PROPERTIES as readonly string[]).includes(property)

export const emptyRouteLevelSelection = (): RouteLevelSelection => ({
  wholeRouteId: [],
  routeSegmentId: [],
  routeId: []
})

export const getAllRouteSegments = (routes: Route[]): Segment[] => routes.flatMap((route) => route.children || [])

export const expandRouteSelection = (routes: Route[], selection: RouteLevelSelection): number[] => {
  const wholeRouteIds = new Set(selection.wholeRouteId)
  const segmentIds = new Set(selection.routeSegmentId)
  const sectionIds = new Set<number>(selection.routeId)
  for (const route of routes) {
    const isWholeRoute = wholeRouteIds.has(route.id)
    for (const segment of route.children || []) {
      if (!isWholeRoute && !segmentIds.has(segment.id)) continue
      for (const section of segment.children || []) sectionIds.add(section.id)
    }
  }
  return [...sectionIds]
}

export const compactRouteSelection = (routes: Route[], sectionIds: number[]): RouteLevelSelection => {
  const selected = new Set(sectionIds)
  const covered = new Set<number>()
  const selection = emptyRouteLevelSelection()
  const isFullySelected = (sections: Section[]): boolean =>
    sections.length > 0 && sections.every((section) => selected.has(section.id))

  for (const route of routes) {
    const routeSections = getRouteSections(route)
    if (isFullySelected(routeSections)) {
      selection.wholeRouteId.push(route.id)
      routeSections.forEach((section) => covered.add(section.id))
      continue
    }
    for (const segment of route.children || []) {
      if (!isFullySelected(segment.children || [])) continue
      selection.routeSegmentId.push(segment.id)
      segment.children.forEach((section) => covered.add(section.id))
    }
  }

  const leftovers = splitRouteIdsByLevel(
    routes,
    sectionIds.filter((id) => !covered.has(id))
  )
  return {
    wholeRouteId: [...selection.wholeRouteId, ...leftovers.wholeRouteId],
    routeSegmentId: [...selection.routeSegmentId, ...leftovers.routeSegmentId],
    routeId: leftovers.routeId
  }
}

export const splitRouteIdsByLevel = (routes: Route[], ids: number[]): RouteLevelSelection => {
  const routeIds = new Set(routes.map((route) => route.id))
  const segmentIds = new Set(getAllRouteSegments(routes).map((segment) => segment.id))
  const selection = emptyRouteLevelSelection()
  for (const id of ids) {
    if (routeIds.has(id)) selection.wholeRouteId.push(id)
    else if (segmentIds.has(id)) selection.routeSegmentId.push(id)
    else selection.routeId.push(id)
  }
  return selection
}

export const findRouteIdsOfSelection = (routes: Route[], selection: RouteLevelSelection): number[] => {
  const wholeRouteIds = new Set(selection.wholeRouteId)
  const segmentIds = new Set(selection.routeSegmentId)
  const sectionIds = new Set(selection.routeId)
  return routes
    .filter(
      (route) =>
        wholeRouteIds.has(route.id) ||
        (route.children || []).some(
          (segment) =>
            segmentIds.has(segment.id) || (segment.children || []).some((section) => sectionIds.has(section.id))
        )
    )
    .map((route) => route.id)
}

export const toRouteOperandNode = (node: ExpressionNode): ExpressionNode => ({
  ...node,
  ...(node.conditions && {
    conditions: node.conditions.map((condition) =>
      isRouteLevelProperty(condition.operand) ? { ...condition, operand: 'routeId' } : condition
    )
  }),
  ...(node.subNodes && { subNodes: node.subNodes.map(toRouteOperandNode) })
})

const routeLevelOfIds = (routes: Route[], ids: number[]): RouteLevelProperty => {
  const selection = splitRouteIdsByLevel(routes, ids)
  return (
    ROUTE_LEVEL_PROPERTIES.find((property) => ids.length > 0 && selection[property].length === ids.length) ?? 'routeId'
  )
}

export const fromRouteOperandNode = (routes: Route[], node: ExpressionNode): ExpressionNode => ({
  ...node,
  ...(node.conditions && {
    conditions: node.conditions.map((condition) => {
      if (condition.operand !== 'routeId') return condition
      const values = Array.isArray(condition.value) ? condition.value : [condition.value]
      return { ...condition, operand: routeLevelOfIds(routes, values.map(Number)) }
    })
  }),
  ...(node.subNodes && { subNodes: node.subNodes.map((subNode) => fromRouteOperandNode(routes, subNode)) })
})

export interface SelectedZone {
  id: number
  name: string
}

/**
 * Lists the zones whose GIS line should be drawn for a route selection. A selected route
 * includes its segments and sections, a selected segment includes its sections. A line can sit
 * at any level, so every level is listed; each zone appears once.
 */
export const collectSelectedZones = (routes: Route[], selection: RouteLevelSelection): SelectedZone[] => {
  const wholeRouteIds = new Set(selection.wholeRouteId)
  const segmentIds = new Set(selection.routeSegmentId)
  const sectionIds = new Set(selection.routeId)
  const result = new Map<number, SelectedZone>()

  const add = (part: { id: number; name: string }): void => {
    if (!result.has(part.id)) result.set(part.id, { id: part.id, name: part.name })
  }

  for (const route of routes) {
    const wholeRoute = wholeRouteIds.has(route.id)
    if (wholeRoute) add(route)
    for (const segment of route.children || []) {
      const wholeSegment = wholeRoute || segmentIds.has(segment.id)
      if (wholeSegment) add(segment)
      for (const section of segment.children || []) {
        if (wholeSegment || sectionIds.has(section.id)) add(section)
      }
    }
  }

  return [...result.values()]
}
